import { Form } from "@unform/web";
import {
  Ambulance,
  BadgeDollarSign,
  BookText,
  CheckCheck,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Contact,
  Files,
  FileSignature,
  PlusCircle,
  Save,
  Trash,
  User,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { saveAs } from "file-saver";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/Enrollment/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/Enrollment/FormHeader";
import Preview from "~/components/Preview";
import {
  countries_list,
  formatter,
  getRegistries,
  handleUpdatedFields,
  sanitizeSpecialChars,
} from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, parseISO } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSearchParams } from "react-router-dom";
import { Scope } from "@unform/core";
import * as Yup from "yup";
import { organizeMultiAndSingleFiles } from "~/functions/uploadPdf";
import FileInputV3, { processEnrollmentFile } from "~/components/RegisterForm/FileInputV3";
import { AlertContext } from "~/App";
import SignaturePad from "react-signature-pad-wrapper";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadString,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { app } from "~/services/firebase";
import { pdfjs } from "react-pdf";

import PDFViewer from "~/components/PDFViewer";
import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import {
  addressOptions,
  dependentRelationshipTypeOptions,
  genderOptions,
  maritalStatusOptions,
  relationshipTypeOptions,
  scheduleOptions,
  sponsorRelationshipTypeOptions,
  yesOrNoOptions,
} from "~/functions/selectPopoverOptions";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import { useSelector } from "react-redux";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@5.4.296/build/pdf.worker.min.mjs`;
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
export const InputContext = createContext({});

const formatPlanScheduleLabel = (label) => {
  if (typeof label !== "string") return label;

  const match = label.match(
    /^(.*?)(\d{1,2}):(\d{2})\s+to\s+(\d{1,2}):(\d{2})$/i
  );
  if (!match) return label;

  const [, description, startHours, startMinutes, endHours, endMinutes] = match;
  let startHour = Number(startHours);
  let endHour = Number(endHours);
  if (
    startHour > 23 ||
    endHour > 23 ||
    Number(startMinutes) > 59 ||
    Number(endMinutes) > 59
  ) {
    return label;
  }

  // Some saved schedules use 12-hour times without AM/PM.
  if (/\bEvening\b/i.test(description)) {
    if (startHour > 0 && startHour < 12) startHour += 12;
    if (endHour > 0 && endHour < 12) endHour += 12;
  } else if (startHour < 12 && endHour < startHour) {
    endHour += 12;
  }

  const displayTime = (hour, minutes) =>
    `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;

  return `${description}${displayTime(startHour, startMinutes)} to ${displayTime(
    endHour,
    endMinutes
  )}`;
};

const planScheduleWeekdays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function getPlanScheduleLabel(schedule) {
  const activeDays = planScheduleWeekdays.filter(
    (day) => schedule?.[day] === true || schedule?.[day] === "true"
  );
  const abbreviations = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const daysText = activeDays.length === 1 ? "1 day" : `${activeDays.length} days`;
  const daysRange =
    activeDays.length === 1
      ? abbreviations[planScheduleWeekdays.indexOf(activeDays[0])]
      : activeDays.length === 7
      ? "Mon - Sun"
      : activeDays.length > 1
      ? `${abbreviations[planScheduleWeekdays.indexOf(activeDays[0])]} - ${
          abbreviations[planScheduleWeekdays.indexOf(activeDays[activeDays.length - 1])]
        }`
      : "";
  const startTime = schedule?.start_time?.substring(0, 5) ?? "";
  const endTime = schedule?.end_time?.substring(0, 5) ?? "";
  return activeDays.length === 4
    ? `${daysText} - ${schedule?.period} - ${startTime} to ${endTime}`
    : `${daysText} - ${schedule?.period} (${daysRange}) - ${startTime} to ${endTime}`;
}

const SANITIZED_FIELDS = {
  students: {
    birth_city: "name",
    birth_state: "name",
    native_language: "name",
    passport_number: "code",
    home_country_address: "address",
    home_country_zip: "code",
    home_country_city: "name",
    home_country_state: "name",
    address: "address",
    zip: "code",
    city: "name",
    state: "name",
  },
  enrollmentemergencies: { name: "person", email: "email" },
  enrollmentdependents: { name: "person", email: "email" },
  enrollmentsponsors: { name: "person", email: "email" },
};

function sanitizeEnrollmentData(data) {
  const sanitizeRecord = (record, fields) => {
    if (!record || typeof record !== "object") return;
    Object.entries(fields).forEach(([field, rule]) => {
      if (typeof record[field] === "string") {
        record[field] = sanitizeSpecialChars(record[field], rule).trim();
      }
    });
  };
  Object.entries(SANITIZED_FIELDS).forEach(([scope, fields]) => {
    const value = data[scope];
    if (Array.isArray(value)) {
      value.forEach((record) => sanitizeRecord(record, fields));
    } else {
      sanitizeRecord(value, fields);
    }
  });
  return data;
}


export default function EnrollmentOutside({
  access = null,
  handleOpened,
  setOpened,
  defaultFormType = "preview",
}) {
  const { profile } = useSelector((state) => state.auth);
  const [searchparams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState({
    enrollmentemergencies: [
      {
        name: "",
        relationship_type: "",
        email: "",
        phone: "",
        signature: {
          url: null,
          created_at: null,
        },
      },
    ],
    agreement: false,
    lastActiveMenu: {},
    activeMenu: null,
    loaded: false,
  });
  const [formType, setFormType] = useState("full");
  const [fullscreen, setFullscreen] = useState(true);
  const [successfullyUpdated, setSuccessfullyUpdated] = useState(true);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  const contentRef = useRef(null);
  const menuContainerRef = useRef(null);
  const fileEnrollmentRefs = useRef([]);
  const [loading, setLoading] = useState(false);
  const [inactivated, setInactivated] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [sent, setSent] = useState(false);
  const [sponsorOtherRelationshipType, setSponsorOtherRelationshipType] =
    useState([]);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < 640 : false);
  const [dynamicScheduleOptions, setDynamicScheduleOptions] = useState([]);
  const [scheduleStartWeekdays, setScheduleStartWeekdays] = useState({});
  const [planScheduleWeekday, setPlanScheduleWeekday] = useState(null);
  const [hasDependentsKey, setHasDependentsKey] = useState(0);
  const [surveyStep, setSurveyStep] = useState(0);
  const [surveyAnswers, setSurveyAnswers] = useState({});
  const [surveyOtherText, setSurveyOtherText] = useState({});
  const mergedScheduleOptions = useMemo(() => {
    const baseOptions =
      dynamicScheduleOptions.length > 0 ? dynamicScheduleOptions : scheduleOptions;
    const optionsMap = new Map(
      (baseOptions || [])
        .filter((option) => option && option.value != null && option.value !== "")
        .map((option) => [
          String(option.value),
          { ...option, label: formatPlanScheduleLabel(option.label) },
        ])
    );

    const savedPlanSchedule = pageData?.plan_schedule;
    if (
      savedPlanSchedule != null &&
      savedPlanSchedule !== "" &&
      !optionsMap.has(String(savedPlanSchedule))
    ) {
      optionsMap.set(String(savedPlanSchedule), {
        value: savedPlanSchedule,
        label: formatPlanScheduleLabel(savedPlanSchedule),
      });
    }

    return Array.from(optionsMap.values());
  }, [dynamicScheduleOptions, pageData?.plan_schedule]);

  const selectedPlanScheduleOption = useMemo(() => {
    const savedPlanSchedule = pageData?.plan_schedule;
    if (savedPlanSchedule != null && savedPlanSchedule !== "") {
      return {
        value: savedPlanSchedule,
        label: formatPlanScheduleLabel(savedPlanSchedule),
      };
    }
    return null;
  }, [pageData?.plan_schedule]);

  const id = searchparams.get("crypt");
  const { alertBox } = useContext(AlertContext);
  const signatureRef = useRef();
  const sponsorshipOptions = [
    { value: true, label: "Yes" },
    { value: false, label: "No (Self Financial Resource)" },
  ];

  const menus = [
    { order: 0, name: "transfer-request" },
    { order: 0, name: "transfer-agent" },
    { order: 1, name: "student-information" },
    { order: 2, name: "emergency-contact" },
    { order: 3, name: "enrollment-information" },
    { order: 4, name: "dependent-information" },
    { order: 5, name: "affidavit-of-support" },
    { order: 6, name: "documents-upload" },
    { order: 7, name: "survey" },
    { order: 8, name: "student-signature" },
    { order: 9, name: "sponsor-signature" },
    { order: 9, name: "finished" },
  ];

  const countriesOptions = countries_list.map((country) => {
    return { value: country, label: country };
  });

  const fullStateName = Yup.string().test(
    "full-state-name",
    "Please enter the full state name (e.g. Florida, not FL).",
    (value) => !value || value.replace(/[^A-Za-z]/g, "").length >= 3
  );

  const studentInfoSchema = Yup.object().shape({
    students: Yup.object().shape({
      birth_state: fullStateName,
      home_country_state: fullStateName,
      state: fullStateName,
    }),
  });

  const emergencySchema = Yup.object().shape({
    enrollmentemergencies: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .matches(
            /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
            "Name can only contain Latin letters."
          )
          .matches(
            /^\s*[\S]+(\s[\S]+)+\s*$/gms,
            "Please enter your full name."
          ),
        relationship_type: Yup.string().required(
          "Relationship Type is required."
        ),
        email: Yup.string().email("Email is invalid."),
        // phone: Yup.string().matches(
        //   /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/,
        //   "Phone number is invalid."
        // ),
      })
    ),
  });

  const dependentInfoSchema = Yup.object().shape({
    has_dependents: Yup.string().required("Required field."),
    enrollmentdependents: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .matches(
            /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
            "Name can only contain Latin letters."
          )
          .matches(
            /^\s*[\S]+(\s[\S]+)+\s*$/gms,
            "Please enter your full name."
          ),
        relationship_type: Yup.string().required(
          "Relationship Type is required."
        ),
        gender: Yup.string().required("Required field."),
        email: Yup.string().email("Email is invalid."),
        // phone: Yup.string().matches(/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/, 'Phone number is invalid.')
      })
    ),
  });

  const affidavitOfSupportSchema = Yup.object().shape({
    need_sponsorship: Yup.string().required("Required field."),
    enrollmentsponsors: Yup.array().of(
      Yup.object().shape({
        name: Yup.string()
          .matches(
            /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi,
            "Name can only contain Latin letters."
          )
          .matches(
            /^\s*[\S]+(\s[\S]+)+\s*$/gms,
            "Please enter your full name."
          ),
        relationship_type: Yup.string().required(
          "Relationship Type is required."
        ),
        email: Yup.string().email("Email is invalid."),
        // phone: Yup.string().matches(/^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/, 'Phone number is invalid.')
      })
    ),
  });

  async function getDocuments(type = "") {
    const { data } = await api.get(
      `/documentsByOrigin?origin=Enrollment&type=${type}&subtype=Student`
    );
    return data;
  }

  async function getDependentDocuments(type = "") {
    const { data } = await api.get(
      `/documentsByOrigin?origin=Enrollment&type=${type}&subtype=Dependent`
    );
    return data;
  }

  async function getPageData() {
    if (id !== "new") {
      try {
        let documents = [];
        let dependentDocuments = [];
        const { data } = await api.get(`/outside/enrollments/${id}`);
        if (data?.status === "Inactive") {
          setInactivated(true);
          setPageData((prev) => ({ ...prev, loaded: true }));
          return;
        }
        const { data: filialData } = await api.get(
          `/filials/${data.filial_id}`
        );
        const savedScheduleById = (filialData?.plan_schedules || []).find(
          (schedule) => String(schedule.id) === String(data.plan_schedule)
        );
        if (savedScheduleById) {
          data.plan_schedule = getPlanScheduleLabel(savedScheduleById);
        }
        documents = await getDocuments(data.students.processsubstatuses.name);
        dependentDocuments = await getDependentDocuments(
          data.students.processsubstatuses.name
        );

        try {
          // Filter the schedules by the student's enrollment type, otherwise
          // Global schedules leak into the other process substatuses.
          const scheduleSubstatusId = data?.students?.processsubstatuses?.id;
          const { data: scheduleStrings } = await api.get(
            `/filials/${data.filial_id}/plan-schedule-strings`,
            scheduleSubstatusId
              ? { params: { processsubstatus_id: scheduleSubstatusId } }
              : undefined
          );
          const scheduleOptionsFromAPI = (scheduleStrings || []).map((str) => ({
            value: str,
            label: str,
          }));
          setDynamicScheduleOptions(scheduleOptionsFromAPI);
          const availableLabels = new Set(scheduleStrings || []);
          const scheduleWeekdays = {};
          (filialData?.plan_schedules || [])
            .filter((schedule) => schedule.active === true || schedule.active === "true")
            .filter((schedule) => {
              const relatedSubstatuses = schedule.processsubstatuses || [];
              return (
                !scheduleSubstatusId ||
                relatedSubstatuses.some(
                  (substatus) => String(substatus.id) === String(scheduleSubstatusId)
                )
              );
            })
            .forEach((schedule) => {
              const label = getPlanScheduleLabel(schedule);
              const firstDay = planScheduleWeekdays.findIndex(
                (day) => schedule[day] === true || schedule[day] === "true"
              );
              if (availableLabels.has(label) && firstDay >= 0) {
                scheduleWeekdays[label] = (firstDay + 1) % 7;
              }
            });
          setScheduleStartWeekdays(scheduleWeekdays);
          setPlanScheduleWeekday(
            scheduleWeekdays[data.plan_schedule] ?? null
          );
        } catch (err) {
          console.log("Error fetching schedule options:", err);
          setDynamicScheduleOptions([]);
          setScheduleStartWeekdays({});
          setPlanScheduleWeekday(null);
        }

        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
        setUploadingDocuments(false);
        setUploadProgress({ current: 0, total: 0 });

        let surveyQuestions = [];
        let surveyAnswered = false;
        let surveyAvailable = false;
        try {
          const { data: surveyData } = await api.get(
            `/outside/enrollments/${id}/survey`
          );
          surveyQuestions = surveyData?.questions || [];
          surveyAnswered = surveyData?.is_answered || false;
          surveyAvailable = surveyData?.survey_available || false;
        } catch (err) {
          console.log("Error fetching survey:", err);
        }

        const isGlobalEnrollment =
          data?.students?.processtypes?.name === "Non-F1" &&
          data?.students?.processsubstatuses?.name === "Global";

        // Global enrollments skip emergency-contact; route it to enrollment-information
        const resolveStep = (step) =>
          isGlobalEnrollment && step === "emergency-contact"
            ? "enrollment-information"
            : step;

        setPageData({
          ...data,
          documents,
          dependentDocuments,
          contracts: filialData.filialdocuments,
          survey_questions: surveyQuestions,
          survey_answered: surveyAnswered,
          survey_available: surveyAvailable,
          loaded: true,
          activeMenu: searchparams.has("activeMenu")
            ? searchparams.get("activeMenu")
            : data.form_step === "transfer-agent"
            ? "student-information"
            : resolveStep(data.form_step),
          lastActiveMenu: searchparams.has("activeMenu")
            ? searchparams.get("activeMenu")
            : menus.find((menu) => menu.name === resolveStep(data.form_step)),
          has_sponsor_signed: data.enrollmentsponsors.find(
            (sponsor) => sponsor.signature !== null
          )
            ? true
            : false,
          signed: data.student_signature ? true : false,
        });
        const getRelationshipTypeOptions = data.enrollmentsponsors.map(
          (sponsor) => {
            return {
              id: sponsor.id,
              value: sponsor.relationship_type,
            };
          }
        );
        setSponsorOtherRelationshipType(getRelationshipTypeOptions);
      } catch (err) {
        if (err.response && err.response.data && err.response.data.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
        console.log(err);
      }
    }
  }

  let isEditable = true;
  // if (profile && pageData.form_step === "finished") {
  //   isEditable = false;
  // }
  if (!profile && pageData.student_signature) {
    isEditable = false;
  }

  useEffect(() => {
    if (!pageData.loaded) {
      getPageData();
    }
  }, [pageData.loaded]);

  useEffect(() => {
    if (pageData.activeMenu === "survey") {
      setSurveyStep(0);
      setSurveyAnswers({});
      setSurveyOtherText({});
    }
  }, [pageData.activeMenu]);

  function getOptionValue(option) {
    const rawValue = option?.option_value;
    if (rawValue !== null && rawValue !== undefined && rawValue !== "") {
      return rawValue;
    }
    return option?.option_description;
  }

  function getOtherOption(question) {
    return (question?.options || []).find((option) => option.is_other) || null;
  }

  function isOtherSelected(question, rawAnswer) {
    const otherOption = getOtherOption(question);
    if (!otherOption) return false;
    const otherValue = getOptionValue(otherOption);
    if (Array.isArray(rawAnswer)) {
      return rawAnswer.some((value) => String(value) === String(otherValue));
    }
    return String(rawAnswer ?? "") === String(otherValue);
  }

  function handleSurveyOtherText(questionId, value) {
    setSurveyOtherText((prev) => ({ ...prev, [questionId]: value }));
    setSuccessfullyUpdated(false);
  }

  function handleSurveyPickOne(question, optionValue) {
    handleSurveyAnswer(question.id, optionValue);
    if (!isOtherSelected(question, optionValue)) {
      handleSurveyOtherText(question.id, "");
    }
  }

  function handleSurveyPickMany(question, optionValue) {
    const current = Array.isArray(surveyAnswers[question.id])
      ? surveyAnswers[question.id]
      : [];
    const exists = current.some(
      (value) => String(value) === String(optionValue)
    );
    const next = exists
      ? current.filter((value) => String(value) !== String(optionValue))
      : [...current, optionValue];
    handleSurveyAnswer(question.id, next);
    if (!isOtherSelected(question, next)) {
      handleSurveyOtherText(question.id, "");
    }
  }

  function isOptionChecked(question, optionValue) {
    const answer = surveyAnswers[question.id];
    if (Array.isArray(answer)) {
      return answer.some((value) => String(value) === String(optionValue));
    }
    return String(answer ?? "") === String(optionValue);
  }

  function isSurveyAnswerEmpty(rawAnswer) {
    return (
      rawAnswer === undefined ||
      rawAnswer === null ||
      rawAnswer === "" ||
      (Array.isArray(rawAnswer) && rawAnswer.length === 0)
    );
  }

  function handleSurveyAnswer(questionId, value) {
    setSurveyAnswers((prev) => ({ ...prev, [questionId]: value }));
    setSuccessfullyUpdated(false);
  }

  function validateSurveyQuestion(question) {
    const rawAnswer = surveyAnswers[question.id];
    if (isSurveyAnswerEmpty(rawAnswer)) {
      toast(`Please answer: "${question.description}"`, {
        type: "error",
        autoClose: 3000,
      });
      return false;
    }
    if (isOtherSelected(question, rawAnswer)) {
      const otherText = (surveyOtherText[question.id] || "").trim();
      if (!otherText) {
        const otherOption = getOtherOption(question);
        toast(
          `Please specify your answer in "${otherOption?.option_description}"`,
          {
            type: "error",
            autoClose: 3000,
          }
        );
        return false;
      }
    }
    return true;
  }

  const surveyComplete = useMemo(() => {
    const questions = pageData.survey_questions || [];
    if (questions.length === 0) return false;
    return questions.every(
      (question) => !isSurveyAnswerEmpty(surveyAnswers[question.id])
    );
  }, [pageData.survey_questions, surveyAnswers]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (pageData.loaded && pageData.activeMenu) {
      scrollContentToTop();
    }
  }, [pageData.activeMenu]);

  useEffect(() => {
    if (pageData.loaded && pageData.activeMenu) {
      setTimeout(scrollMenuToActive, 100);
    }
  }, [pageData.activeMenu]);

  // Global enrollment-information is read-only: there's nothing to edit, so the
  // form never flips to "unsaved". When the user lands on this step, unlock the
  // save button once so they can submit/advance without changing anything.
  // Runs only on step/load changes (not on successfullyUpdated) so every other
  // tab keeps the default behavior of staying "Saved" until edited.
  useEffect(() => {
    const isGlobalEnrollment =
      pageData?.students?.processtypes?.name === "Non-F1" &&
      pageData?.students?.processsubstatuses?.name === "Global";
    if (
      pageData.loaded &&
      isGlobalEnrollment &&
      pageData.activeMenu === "enrollment-information"
    ) {
      setSuccessfullyUpdated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageData.loaded,
    pageData.activeMenu,
    pageData?.students?.processtypes?.name,
    pageData?.students?.processsubstatuses?.name,
  ]);

  async function handleGeneralFormSubmit(data) {
    sanitizeEnrollmentData(data);
    scrollContentToTop();
    setLoading(true);
    try {
      generalForm.current.setErrors({});
      if (pageData.activeMenu === "student-information") {
        await studentInfoSchema.validate(data, {
          abortEarly: false,
        });
      } else if (pageData.activeMenu === "emergency-contact") {
        await emergencySchema.validate(data, {
          abortEarly: false,
        });
      } else if (pageData.activeMenu === "dependent-information") {
        await dependentInfoSchema.validate(data, {
          abortEarly: false,
        });
      } else if (pageData.activeMenu === "affidavit-of-support") {
        await affidavitOfSupportSchema.validate(data, {
          abortEarly: false,
        });
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        generalForm.current.setErrors(validationErrors);
      }
      setLoading(false);
      return;
    }
    delete data.agreement;
    // Global enrollment-information is read-only: force saving to advance the step
    const isReadOnlyGlobalStep =
      isGlobal && pageData.activeMenu === "enrollment-information";
    if (successfullyUpdated && !isReadOnlyGlobalStep) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }

    if (id !== "new") {
      if (pageData.activeMenu === "documents-upload") {
        const missingRequired = (pageData.documents || []).filter((doc) => {
          if (doc.active === false) return false;
          if (doc.short_name === "St.Marriage certificate" && pageData.students?.marital_status !== "Married") return false;
          if (!doc.required) return false;
          const hasSaved = (pageData.enrollmentdocuments || []).some((ed) => ed.document_id === doc.id);
          const hasPending = fileEnrollmentRefs.current.some((r) => {
            return r?.documentId === doc.id && r?.hasPending();
          });
          return !hasSaved && !hasPending;
        });
        if (missingRequired.length > 0) {
          const names = missingRequired.map((d) => d.title).join(", ");
          toast(`The following required documents are missing: ${names}`, {
            type: "error",
            autoClose: 5000,
          });
          setLoading(false);
          return;
        }
      }

      if (pageData.activeMenu === "dependent-information" && pageData.has_dependents) {
        const missingDepRequired = [];
        for (const dep of (pageData.enrollmentdependents || [])) {
          for (const doc of (pageData.dependentDocuments || []).filter((d) => d.active !== false && d.required)) {
            const hasSaved = (dep.documents || []).some((d) => d.document_id === doc.id);
            const hasPending = (dep.pendingFiles || []).some((pf) => pf.documentId === doc.id);
            if (!hasSaved && !hasPending) {
              missingDepRequired.push(doc.title);
            }
          }
        }
        if (missingDepRequired.length > 0) {
          const names = [...new Set(missingDepRequired)].join(", ");
          toast(`The following required documents are missing: ${names}`, { type: "error", autoClose: 5000 });
          setLoading(false);
          return;
        }
      }

      if (pageData.activeMenu === "student-signature") {
        const signaturePad = signatureRef.current?.instance;
        if (!pageData.signature && (!signaturePad || signaturePad.isEmpty())) {
          toast("Student signature is required.", {
            type: "error",
            autoClose: 3000,
          });
          setLoading(false);
          return;
        }
        const signature = signatureRef.current.toDataURL();

        const fileUuid = uuidv4();
        const storage = getStorage(app);
        const local = "Enrollments/Signatures/" + fileUuid + ".png";
        const imageRef = ref(storage, local);
        await uploadString(imageRef, signature.substring(22), "base64").then(
          async (snapshot) => {
            await getDownloadURL(snapshot.ref).then(async (downloadURL) => {
              await api.post(`/enrollmentstudentsignature`, {
                enrollment_id: id,
                files: {
                  url: downloadURL,
                  name: fileUuid + ".png",
                  size: signature.length,
                  key: fileUuid + ".png",
                },
              });
              await api.put(`/outside/enrollments/${id}`, {
                activeMenu: pageData.activeMenu,
                lastActiveMenu: pageData.lastActiveMenu,
              });
              scrollContentToTop();
              setPageData({ ...pageData, loaded: false });
              setSuccessfullyUpdated(true);
              toast("Saved!", { autoClose: 1000 });
              setLoading(false);
            });
          }
        );
        return;
      }

      if (pageData.activeMenu === "survey") {
        if (pageData.survey_answered) {
          await api.put(`/outside/enrollments/${id}`, {
            activeMenu: pageData.activeMenu,
            lastActiveMenu: pageData.lastActiveMenu,
          });
          scrollContentToTop();
          setPageData({ ...pageData, loaded: false });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          setLoading(false);
          return;
        }

        const answers = [];
        const surveyQuestionList = pageData.survey_questions || [];
        for (let index = 0; index < surveyQuestionList.length; index++) {
          const question = surveyQuestionList[index];
          const rawAnswer = surveyAnswers[question.id];
          const isEmpty =
            rawAnswer === undefined ||
            rawAnswer === null ||
            rawAnswer === "" ||
            (Array.isArray(rawAnswer) && rawAnswer.length === 0);
          if (isEmpty) {
            setSurveyStep(index);
            toast("Please answer all the survey questions.", {
              type: "error",
              autoClose: 3000,
            });
            setLoading(false);
            return;
          }

          const answerItem = {
            id: question.id,
            description: question.description,
            answer: rawAnswer,
          };

          if (isOtherSelected(question, rawAnswer)) {
            const otherText = (surveyOtherText[question.id] || "").trim();
            if (!otherText) {
              setSurveyStep(index);
              toast("Please fill in the 'Other' answer.", {
                type: "error",
                autoClose: 3000,
              });
              setLoading(false);
              return;
            }
            answerItem.other_text = otherText;
          }

          answers.push(answerItem);
        }

        console.log("Survey payload:", { answers });

        try {
          await api.post(`/outside/enrollments/${id}/survey`, { answers });
          await api.put(`/outside/enrollments/${id}`, {
            activeMenu: pageData.activeMenu,
            lastActiveMenu: pageData.lastActiveMenu,
          });
          scrollContentToTop();
          setPageData({ ...pageData, loaded: false });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
        } catch (err) {
          toast(err?.response?.data?.error || "Save failed.", {
            type: "error",
            autoClose: 3000,
          });
        }
        setLoading(false);
        return;
      }

      if (pageData.activeMenu === "documents-upload") {
        const refsWithPending = fileEnrollmentRefs.current.filter((r) => r?.hasPending());
        if (refsWithPending.length > 0) {
          const total = refsWithPending.reduce((sum, r) => sum + (r?.pendingCount() || 0), 0);
          let current = 0;
          setUploadProgress({ current: 0, total });
          setUploadingDocuments(true);
          const toastId = toast.loading(`Uploading files... 0/${total} (0%)`, { autoClose: false });
          try {
            for (const r of refsWithPending) {
              await r.uploadPending(() => {
                current += 1;
                const pct = Math.round((current / total) * 100);
                setUploadProgress({ current, total });
                toast.update(toastId, { render: `Uploading files... ${current}/${total} (${pct}%)` });
              });
            }
            toast.update(toastId, { render: `All ${total} file${total > 1 ? "s" : ""} uploaded!`, type: "success", autoClose: 3000, isLoading: false });
          } catch (err) {
            toast.update(toastId, { render: err?.response?.data?.error || "Upload failed.", type: "error", autoClose: 3000, isLoading: false });
            setUploadingDocuments(false);
            setLoading(false);
            return;
          }
        }
      }

      const updated = handleUpdatedFields(data, pageData);

      if (updated.length > 0) {
        const objUpdated = Object.fromEntries(updated);
        const { date_of_birth, passport_expiration_date, i94_expiration_date } =
          pageData.students;
        const normalizeDateValue = (value) => {
          if (!value) return null;
          if (value instanceof Date) return value;
          if (typeof value === "string") return parseISO(value);
          return value;
        };
        const normalizedDateOfBirth = normalizeDateValue(date_of_birth);
        const normalizedPassportExpirationDate = normalizeDateValue(
          passport_expiration_date
        );
        const normalizedI94ExpirationDate =
          normalizeDateValue(i94_expiration_date);

        delete objUpdated.documents;

        try {
          await api.put(`/outside/enrollments/${id}`, {
            ...objUpdated,
            activeMenu: pageData.activeMenu,
            lastActiveMenu: pageData.lastActiveMenu,
            date_of_birth: normalizedDateOfBirth
              ? format(normalizedDateOfBirth, "yyyy-MM-dd")
              : null,
            passport_expiration_date: normalizedPassportExpirationDate
              ? format(normalizedPassportExpirationDate, "yyyy-MM-dd")
              : null,
            i94_expiration_date: normalizedI94ExpirationDate
              ? format(normalizedI94ExpirationDate, "yyyy-MM-dd")
              : null,
          });
          const dependentsWithPending = (pageData.enrollmentdependents || []).filter(
            (dep) => (dep.pendingFiles || []).length > 0
          );
          if (dependentsWithPending.length > 0) {
            const toastId = toast.loading("Dependents files are being uploaded...");
            try {
              for (const dep of dependentsWithPending) {
                for (const { file, documentId } of dep.pendingFiles) {
                  const processedFile = await processEnrollmentFile(file, documentId);
                  await api.post(`/dependentsdocuments`, {
                    enrollment_id: id,
                    files: processedFile,
                    dependent_id: dep.id,
                  });
                }
              }
              toast.update(toastId, { render: "All files have been uploaded!", type: "success", autoClose: 3000, isLoading: false });
            } catch (err) {
              toast.update(toastId, { render: err?.response?.data?.error || "Upload failed.", type: "error", autoClose: 3000, isLoading: false });
              setLoading(false);
              return;
            }
          }
          scrollContentToTop();
          setUploadingDocuments(pageData.activeMenu === "documents-upload");
          setPageData({ ...pageData, loaded: false });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
        } catch (err) {
          toast(err?.response?.data?.error || "Save failed.", { type: "error", autoClose: 3000 });
          setUploadingDocuments(false);
          setUploadProgress({ current: 0, total: 0 });
          setLoading(false);
          return;
        }
        setLoading(false);
      } else {
        try {
          await api.put(`/outside/enrollments/${id}`, {
            activeMenu: pageData.activeMenu,
            lastActiveMenu: pageData.lastActiveMenu,
          });
          const dependentsWithPending = (pageData.enrollmentdependents || []).filter(
            (dep) => (dep.pendingFiles || []).length > 0
          );
          if (dependentsWithPending.length > 0) {
            const toastId = toast.loading("Dependents files are being uploaded...");
            try {
              for (const dep of dependentsWithPending) {
                for (const { file, documentId } of dep.pendingFiles) {
                  const processedFile = await processEnrollmentFile(file, documentId);
                  await api.post(`/dependentsdocuments`, {
                    enrollment_id: id,
                    files: processedFile,
                    dependent_id: dep.id,
                  });
                }
              }
              toast.update(toastId, { render: "All files have been uploaded!", type: "success", autoClose: 3000, isLoading: false });
            } catch (err) {
              toast.update(toastId, { render: err?.response?.data?.error || "Upload failed.", type: "error", autoClose: 3000, isLoading: false });
              setUploadingDocuments(false);
              setUploadProgress({ current: 0, total: 0 });
              setLoading(false);
              return;
            }
          }
          scrollContentToTop();
          setUploadingDocuments(pageData.activeMenu === "documents-upload");
          setPageData({ ...pageData, loaded: false });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
        } catch (err) {
          toast(err?.response?.data?.error || "Save failed.", { type: "error", autoClose: 3000 });
          setUploadingDocuments(false);
          setUploadProgress({ current: 0, total: 0 });
        }
        setLoading(false);
      }
    }
  }

  function verifyDependentDocuments(data = null) {
    if (data.enrollmentdependents && data.enrollmentdependents.length > 0) {
      let toastId = null;
      data.enrollmentdependents.map((dependent, index) => {
        if (
          dependent.dependentDocuments.find(
            (document) =>
              (typeof document.file_id === "undefined" && document.file_id) ||
              (typeof document.file_id === "object" &&
                Array.from(document.file_id).length > 0)
          )
        ) {
          toastId = toast.loading("Dependents files are being uploaded...");
        }
        const allPromises = organizeMultiAndSingleFiles(
          dependent.dependentDocuments,
          "Dependents"
        );
        data.enrollmentdependents[index].storedFiles = [];
        Promise.all(allPromises)
          .then(async (files) => {
            try {
              files.map(async (file) => {
                if (!file) {
                  return;
                }
                if (file.name) {
                  data.enrollmentdependents[index].storedFiles.push(file);
                  await api.post(`/dependentsdocuments`, {
                    enrollment_id: id,
                    files: file,
                    dependent_id: dependent.id,
                  });
                  toastId &&
                    toast.update(toastId, {
                      render: "All files have been uploaded!",
                      type: "success",
                      autoClose: 3000,
                      isLoading: false,
                    });
                } else {
                  file
                    .sort((a, b) => a.size > b.size)
                    .map(async (promise, index) => {
                      await Promise.all([promise]).then(async (singleFile) => {
                        if (index + 1 === file.length) {
                          toastId &&
                            toast.update(toastId, {
                              render: "All files have been uploaded!",
                              type: "success",
                              autoClose: 3000,
                              isLoading: false,
                            });
                        }
                        data.enrollmentdependents[index].storedFiles.push(
                          singleFile[0]
                        );
                        await api.post(`/dependentsdocuments`, {
                          enrollment_id: id,
                          files: singleFile[0],
                          dependent_id: dependent.id,
                        });
                      });
                    });
                }
              });
            } catch (err) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          })
          .finally(() => {
            return data;
          });
      });
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleInactivate() {}

  async function handleHasDependents(el) {
    if (el.value === null || el.value === undefined) {
      return;
    }
    if (pageData.has_dependents === el.value && pageData.form_step !== "dependent-information") {
      return;
    }

    if (pageData.has_dependents === true && el.value === false) {
      alertBox({
        title: "Attention!",
        descriptionHTML: `<p>Are you sure you want to remove all dependents? This action will delete all dependent data.</p>`,
        buttons: [
          {
            title: "No",
            class: "cancel",
            onPress: () => {
              setHasDependentsKey((k) => k + 1);
            },
          },
          {
            title: "Yes",
            onPress: async () => {
              setSuccessfullyUpdated(false);
              let dependentIndex = 0;
              for (let dependent of pageData.enrollmentdependents) {
                await handleRemoveDependent(dependentIndex, dependent.id);
                dependentIndex++;
              }
              setPageData({
                ...pageData,
                has_dependents: false,
                enrollmentdependents: [],
              });
            },
          },
        ],
      });
      return;
    }

    setSuccessfullyUpdated(false);
    let dependentIndex = 0;
    for (let dependent of pageData.enrollmentdependents) {
      await handleRemoveDependent(dependentIndex, dependent.id);
      dependentIndex++;
    }
    if (el.value === false) {
      setPageData({
        ...pageData,
        has_dependents: el.value,
        enrollmentdependents: [],
      });
    } else {
      const newDependent = await handleCreateDependent();
      setPageData({
        ...pageData,
        has_dependents: el.value,
        enrollmentdependents: [newDependent],
      });
    }
  }

  async function handleHasSponsors(el) {
    setSuccessfullyUpdated(false);
    if (pageData.need_sponsorship === el.value) {
      return;
    }
    let newSponsor = null;
    async function resetSponsors() {
      for (let sponsor of pageData.enrollmentsponsors) {
        await handleRemoveSponsor(sponsor.id);
      }
    }
    if (el.value === false) {
      await resetSponsors();
    } else {
      if (pageData.enrollmentsponsors.length > 0) {
        await resetSponsors();
      }
      newSponsor = await handleCreateSponsor();
    }
    setPageData({
      ...pageData,
      need_sponsorship: el.value,
      enrollmentsponsors: el.value ? [newSponsor] : [],
    });
  }

  async function handleCreateDependent() {
    try {
      const { data } = await api.post(`/enrollmentdependent`, {
        enrollment_id: id,
      });
      return data;
    } catch (err) {
      console.log({ err });
      // toast(err.response.data.error, { type: "error", autoClose: 3000 });
      return null;
    }
  }

  async function handleCreateSponsor() {
    try {
      const { data } = await api.post(`/enrollmentsponsor`, {
        enrollment_id: id,
      });
      return data;
    } catch (err) {
      console.log({ err });
      toast(err.response.data.error, { type: "error", autoClose: 3000 });
      return null;
    }
  }

  async function handleAddDependent() {
    setSuccessfullyUpdated(false);
    const addedDependents = [...pageData.enrollmentdependents];
    addedDependents.push(await handleCreateDependent());
    setPageData({ ...pageData, enrollmentdependents: addedDependents });
  }

  async function handleAddSponsor() {
    setSuccessfullyUpdated(false);
    const addedSponsors = [...pageData.enrollmentsponsors];
    addedSponsors.push(await handleCreateSponsor());
    setPageData({ ...pageData, enrollmentsponsors: addedSponsors });
  }

  async function handleRemoveDependent(index, id) {
    setSuccessfullyUpdated(false);

    try {
      if (id) {
        await api.delete(`/enrollmentdependent/${id}`);
      }
      const newData = generalForm.current.getData();

      if (newData.enrollmentdependents) {
        newData.enrollmentdependents.splice(index, 1);
        generalForm.current.setData(newData);

        setPageData({
          ...pageData,
          enrollmentdependents: newData.enrollmentdependents,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  async function handleRemoveSponsor(index, id) {
    setSuccessfullyUpdated(false);

    try {
      if (id) {
        await api.delete(`/enrollmentsponsor/${id}`);
      }

      const newData = generalForm.current.getData();

      if (newData.enrollmentsponsors) {
        newData.enrollmentsponsors.splice(index, 1);
        generalForm.current.setData(newData);
        setPageData({
          ...pageData,
          enrollmentsponsors: newData.enrollmentsponsors,
        });
      }
    } catch (err) {
      console.log(err);
    }
  }

  function handleDeleteDependentDocument(dependent_id, id) {
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              if (id) {
                await api.delete(`/dependentsdocuments/${id}`);
              }
              toast("File deleted!", { autoClose: 1000 });
              const newDependents = pageData.enrollmentdependents.map(
                (dependent) => {
                  if (dependent.id === dependent_id) {
                    const newDocuments = dependent.documents.filter(
                      (document) => document.id !== id
                    );
                    return { ...dependent, documents: newDocuments };
                  } else {
                    return dependent;
                  }
                }
              );
              setPageData({
                ...pageData,
                enrollmentdependents: newDependents,
              });
            } catch (err) {
              console.log(err);
              // toast(err.response.data.error, {
              //   type: "error",
              //   autoClose: 3000,
              // });
            }
          },
        },
      ],
    });
  }

  async function handleDeleteDocumentDirect(id) {
    try {
      await api.delete(`/enrollmentdocuments/${id}`);
      setPageData((prev) => ({
        ...prev,
        enrollmentdocuments: prev.enrollmentdocuments.filter(
          (enrollmentdocument) => enrollmentdocument.id !== id
        ),
      }));
    } catch (err) {
      toast(err.response?.data?.error || "Error deleting file", {
        type: "error",
        autoClose: 3000,
      });
    }
  }

  function handleDeleteDocument(id) {
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/enrollmentdocuments/${id}`);
              toast("File deleted!", { autoClose: 1000 });
              setPageData((prev) => ({
                ...prev,
                enrollmentdocuments: prev.enrollmentdocuments.filter(
                  (enrollmentdocument) => enrollmentdocument.id !== id
                ),
              }));
            } catch (err) {
              toast(err.response?.data?.error || "Error deleting file", {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  function handleClearSignature() {
    const signaturePad = signatureRef.current;

    if (signaturePad) {
      signaturePad.instance.clear();
      setSuccessfullyUpdated(true);
    }
  }

  function handleGetAffidavitSupport(sponsorId = null) {
    if (!sponsorId) {
      return;
    }
    api
      .get(`/pdf/affidavit-support/${sponsorId}`, {
        responseType: "blob",
      })
      .then((res) => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        saveAs(
          pdfBlob,
          `affidavit_of_support_${"bc59904a-686e-4b05-b69f-64960af78565"}.pdf`
        );
      })
      .catch((err) => {
        toast("Affidavit not available yet.", {
          type: "error",
          autoClose: 3000,
        });
      });
  }

  function getVisibleMenus() {
    return menus.filter((menu) => {
      if (
        !isF1 &&
        (menu.name === "dependent-information" ||
          menu.name === "affidavit-of-support")
      ) {
        return false;
      }
      if (isGlobal && menu.name === "emergency-contact") {
        return false;
      }
      if (menu.name === "transfer-request" || menu.name === "transfer-agent")
        return false;
      if (menu.name === "sponsor-signature") return false;
      if (menu.name === "survey" && !pageData.survey_available) return false;
      return true;
    });
  }

  function scrollMenuToActive() {
    if (menuContainerRef.current && pageData.activeMenu) {
      const activeButton = menuContainerRef.current.querySelector(
        `[data-menu-name="${pageData.activeMenu}"]`
      );
      if (activeButton) {
        activeButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    }
  }

  function scrollContentToTop() {
    if (
      contentRef.current &&
      typeof contentRef.current.scrollTo === "function"
    ) {
      contentRef.current.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }

  function handleNextMenu() {
    if (!successfullyUpdated) {
      toast.info("Please save your changes before continuing.");
      return;
    }

    const visibleMenus = getVisibleMenus();
    const currentIndex = visibleMenus.findIndex(
      (menu) => menu.name === pageData.activeMenu
    );
    if (currentIndex < visibleMenus.length - 1) {
      const nextMenu = visibleMenus[currentIndex + 1];
      if (
        pageData.lastActiveMenu &&
        nextMenu.order > pageData.lastActiveMenu.order
      ) {
        toast.info("Please complete the current section first.");
        return;
      }
      setPageData({ ...pageData, activeMenu: nextMenu.name });
      scrollContentToTop();
      setTimeout(scrollMenuToActive, 100);
    }
  }

  function handlePreviousMenu() {
    const visibleMenus = getVisibleMenus();
    const currentIndex = visibleMenus.findIndex(
      (menu) => menu.name === pageData.activeMenu
    );
    if (currentIndex > 0) {
      const previousMenu = visibleMenus[currentIndex - 1];
      setPageData({ ...pageData, activeMenu: previousMenu.name });
      scrollContentToTop();
      setTimeout(scrollMenuToActive, 100);
    }
  }

  function isFirstMenu() {
    const visibleMenus = getVisibleMenus();
    return pageData.activeMenu === visibleMenus[0]?.name;
  }

  function isLastMenu() {
    const visibleMenus = getVisibleMenus();
    return pageData.activeMenu === visibleMenus[visibleMenus.length - 1]?.name;
  }

  const isF1 = pageData?.students?.processtypes?.name === "F1";

  const isGlobal =
    pageData?.students?.processtypes?.name === "Non-F1" &&
    pageData?.students?.processsubstatuses?.name === "Global";

  const isTransfer =
    pageData?.students?.processsubstatuses?.name === "Transfer";
  const isInitial =
    pageData?.students?.processsubstatuses?.name === "Initial Visa";
  const isReinstatement =
    pageData?.students?.processsubstatuses?.name === "Reinstatement";

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {inactivated ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-6 p-8 text-center">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
              <X size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-800">Enrollment Inactivated</h2>
            <p className="text-sm text-gray-500 max-w-sm">
              This enrollment process has been inactivated. Please contact your agent to request a new enrollment link.
            </p>
          </div>
        </div>
      ) : !sent && pageData.loaded ? (
        <div className="flex h-full flex-col items-start justify-between gap-4 lg:flex-row w-full">
          <div
            ref={menuContainerRef}
            className="flex flex-row lg:flex-col items-center justify-start lg:justify-between text-xs w-full lg:w-32 gap-2 lg:gap-4 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0"
          >
            <RegisterFormMenu
              disabled={false}
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: menus[2].name })
              }
              activeMenu={pageData.activeMenu}
              name="student-information"
            >
              <User size={22} /> Student Information
            </RegisterFormMenu>
            {!isGlobal && (
              <RegisterFormMenu
                disabled={
                  pageData.lastActiveMenu &&
                  pageData.lastActiveMenu.order < 2 &&
                  !searchparams.has("activeMenu")
                }
                setActiveMenu={() =>
                  setPageData({ ...pageData, activeMenu: menus[3].name })
                }
                activeMenu={pageData.activeMenu}
                name="emergency-contact"
              >
                <Ambulance size={22} /> Emergency Contact
              </RegisterFormMenu>
            )}
            <RegisterFormMenu
              disabled={
                pageData.lastActiveMenu &&
                pageData.lastActiveMenu.order < 3 &&
                !searchparams.has("activeMenu")
              }
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: menus[4].name })
              }
              activeMenu={pageData.activeMenu}
              name="enrollment-information"
            >
              <BookText size={22} /> Enrollment Information
            </RegisterFormMenu>
            {isF1 && (
              <RegisterFormMenu
                disabled={
                  pageData.lastActiveMenu &&
                  pageData.lastActiveMenu.order < 4 &&
                  !searchparams.has("activeMenu")
                }
                setActiveMenu={() =>
                  setPageData({ ...pageData, activeMenu: menus[5].name })
                }
                activeMenu={pageData.activeMenu}
                name="dependent-information"
              >
                <Contact size={22} /> Dependent Information
              </RegisterFormMenu>
            )}
            {isF1 && (
              <RegisterFormMenu
                disabled={
                  pageData.lastActiveMenu &&
                  pageData.lastActiveMenu.order < 5 &&
                  !searchparams.has("activeMenu")
                }
                setActiveMenu={() =>
                  setPageData({ ...pageData, activeMenu: menus[6].name })
                }
                activeMenu={pageData.activeMenu}
                name="affidavit-of-support"
              >
                <BadgeDollarSign size={22} /> Affidavit of Support
              </RegisterFormMenu>
            )}
            <RegisterFormMenu
              disabled={
                pageData.lastActiveMenu &&
                pageData.lastActiveMenu.order < 6 &&
                !searchparams.has("activeMenu")
              }
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: menus[7].name })
              }
              activeMenu={pageData.activeMenu}
              name="documents-upload"
            >
              <Files size={22} /> Documents Upload
            </RegisterFormMenu>
            {pageData.survey_available && (
              <RegisterFormMenu
                disabled={
                  pageData.lastActiveMenu &&
                  pageData.lastActiveMenu.order < 7 &&
                  !searchparams.has("activeMenu")
                }
                setActiveMenu={() =>
                  setPageData({ ...pageData, activeMenu: menus[8].name })
                }
                activeMenu={pageData.activeMenu}
                name="survey"
              >
                <ClipboardList size={22} /> Survey
              </RegisterFormMenu>
            )}
            <RegisterFormMenu
              disabled={
                pageData.lastActiveMenu &&
                pageData.lastActiveMenu.order < 8 &&
                !searchparams.has("activeMenu")
              }
              setActiveMenu={() =>
                setPageData({ ...pageData, activeMenu: menus[9].name })
              }
              activeMenu={pageData.activeMenu}
              name="student-signature"
            >
              <FileSignature size={22} /> Student's Signature
            </RegisterFormMenu>
            <RegisterFormMenu
              disabled={
                pageData.lastActiveMenu &&
                pageData.lastActiveMenu.order < 9 &&
                !searchparams.has("activeMenu")
              }
              setActiveMenu={() => null}
              activeMenu={pageData.activeMenu}
              name="sponsor-signature"
            >
              <CheckCheck size={18} /> Finished
            </RegisterFormMenu>
          </div>
          <div className="relative border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start w-full lg:w-auto">
            {uploadingDocuments && (
              <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-white gap-6">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                    <circle cx="40" cy="40" r="34" fill="none" stroke="#f0f0f0" strokeWidth="8" />
                    <circle
                      cx="40" cy="40" r="34" fill="none"
                      stroke="#F97316"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 34}`}
                      strokeDashoffset={`${2 * Math.PI * 34 * (1 - (uploadProgress.total > 0 ? uploadProgress.current / uploadProgress.total : 0))}`}
                      style={{ transition: "stroke-dashoffset 0.4s ease" }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold text-mila_orange">
                      {uploadProgress.total > 0 ? Math.round((uploadProgress.current / uploadProgress.total) * 100) : 0}%
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-sm font-semibold text-gray-700">Uploading documents...</span>
                  {uploadProgress.total > 0 && (
                    <span className="text-xs text-gray-400">{uploadProgress.current} of {uploadProgress.total} file{uploadProgress.total > 1 ? "s" : ""}</span>
                  )}
                </div>
              </div>
            )}
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                handleInactivate,
                handleOutsideMail: null,
                canceled: pageData.canceled_at,
              }}
            >
              <div
                ref={contentRef}
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll h-full"
              >
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <FormHeader
                    saveText="Save & Continue"
                    outside={true}
                    loading={loading}
                    disableSave={
                      pageData.activeMenu === "survey" &&
                      pageData.survey_available &&
                      !pageData.survey_answered &&
                      (pageData.survey_questions || []).length > 0 &&
                      !surveyComplete
                    }
                    access={{
                      view: true,
                      edit: true,
                      create: true,
                      inactivate: true,
                    }}
                    title={
                      pageData.students.name +
                      " " +
                      pageData.students.last_name +
                      " - Enrollment Process"
                    }
                    registry={registry}
                    InputContext={InputContext}
                  />
                  {pageData.loaded ? (
                    <>
                      {pageData.activeMenu === "student-information" && (
                        <InputLineGroup
                          title="Student Information"
                          activeMenu={
                            pageData.activeMenu === "student-information"
                          }
                        >
                          {isReinstatement && (
                            <Scope path={`reinstatement`}>
                              <InputLine title="Reinstatement Information">
                                <div className="flex flex-col w-full">
                                  <PDFViewer
                                    download={true}
                                    file={{
                                      url: "/assets/mila-reinstatement-form.pdf",
                                    }}
                                    zoomable={false}
                                    rotateable={false}
                                    className="flex-1"
                                    maxWidth={800}
                                  />
                                  <CheckboxInput
                                    name="agreement"
                                    required={true}
                                    title="I agree to the terms and conditions above"
                                    InputContext={InputContext}
                                    grow
                                    readOnly={
                                      pageData.reinstatement_declaration_aggreement
                                        ? true
                                        : false
                                    }
                                    defaultValue={
                                      pageData.reinstatement_declaration_aggreement
                                        ? true
                                        : false
                                    }
                                  />
                                </div>
                              </InputLine>
                            </Scope>
                          )}
                          <Scope path={`students`}>
                            <InputLine title="General Data">
                              <DatePicker
                                name="date_of_birth"
                                required
                                grow
                                title="Birth Date"
                                readOnly={!isEditable}
                                defaultValue={
                                  pageData.students.date_of_birth
                                    ? parseISO(pageData.students.date_of_birth)
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                onKeyDown={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onChangeRaw={(e) => e.preventDefault()}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="marital_status"
                                required
                                grow
                                title="Marital Status"
                                isSearchable
                                readOnly={!isEditable}
                                defaultValue={maritalStatusOptions.find(
                                  (maritalStatus) =>
                                    maritalStatus.value ===
                                    pageData.students.marital_status
                                )}
                                options={maritalStatusOptions}
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>
                          <Scope path={`students`}>
                            <InputLine title="Birth Details">
                              <Input
                                type="text"
                                sanitize="name"
                                name="birth_city"
                                required
                                grow
                                title="Birth City"
                                readOnly={!isEditable}
                                defaultValue={pageData.students.birth_city}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                sanitize="name"
                                name="birth_state"
                                required
                                grow
                                title="Birth State"
                                readOnly={!isEditable}
                                defaultValue={pageData.students.birth_state}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="birth_country"
                                required
                                grow
                                title="Birth Country"
                                isSearchable
                                readOnly={!isEditable}
                                defaultValue={countriesOptions.find(
                                  (country) =>
                                    country.value ===
                                    pageData.students.birth_country
                                )}
                                options={countriesOptions}
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                sanitize="name"
                                name="native_language"
                                required
                                grow
                                readOnly={!isEditable}
                                title="Native Language"
                                defaultValue={pageData.students.native_language}
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="citizen_country"
                                required
                                grow
                                title="Citizen Country"
                                isSearchable
                                readOnly={!isEditable}
                                defaultValue={countriesOptions.find(
                                  (country) =>
                                    country.value ===
                                    pageData.students.citizen_country
                                )}
                                options={countriesOptions}
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine title="Documentation">
                              <Input
                                type="text"
                                sanitize="code"
                                name="passport_number"
                                required
                                grow
                                readOnly={!isEditable}
                                title="Passport Number"
                                placeholder="-----"
                                defaultValue={pageData.students.passport_number}
                                InputContext={InputContext}
                              />
                              <DatePicker
                                name="passport_expiration_date"
                                required
                                grow
                                readOnly={!isEditable}
                                title="Passport Expiration Date"
                                defaultValue={
                                  pageData.students.passport_expiration_date
                                    ? parseISO(
                                        pageData.students
                                          .passport_expiration_date
                                      )
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                onKeyDown={(e) => e.preventDefault()}
                                onPaste={(e) => e.preventDefault()}
                                onChangeRaw={(e) => e.preventDefault()}
                                InputContext={InputContext}
                              />
                              {pageData.students.sub_status ===
                                "Change of Visa Status" && (
                                <DatePicker
                                  name="i94_expiration_date"
                                  required
                                  grow
                                  readOnly={!isEditable}
                                  title="I94 Expiration Date"
                                  defaultValue={
                                    pageData.students.i94_expiration_date
                                      ? parseISO(
                                          pageData.students.i94_expiration_date
                                        )
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              )}
                            </InputLine>
                          </Scope>

                          <InputLine title="Admission Correspondence">
                            <SelectPopover
                              name="admission_correspondence_address"
                              required
                              grow
                              readOnly={!isEditable}
                              title="Please check the box where you wish your admission correspondence to be mailed"
                              options={
                                isInitial
                                  ? [addressOptions[1]]
                                  : isTransfer
                                  ? [addressOptions[0]]
                                  : addressOptions
                              }
                              defaultValue={addressOptions.find(
                                (address) =>
                                  address.value ===
                                  pageData.admission_correspondence_address
                              )}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <Scope path={`students`}>
                            {!isTransfer && (
                              <InputLine title="Address in Home Country">
                                <PhoneNumberInput
                                  type="text"
                                  name="home_country_phone"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="Phone Number"
                                  value={pageData.students.home_country_phone}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="address"
                                  name="home_country_address"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="Address"
                                  defaultValue={
                                    pageData.students.home_country_address
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="code"
                                  name="home_country_zip"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="Zip Code"
                                  defaultValue={
                                    pageData.students.home_country_zip
                                  }
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            )}
                            {!isTransfer && (
                              <InputLine>
                                <Input
                                  type="text"
                                  sanitize="name"
                                  name="home_country_city"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="City"
                                  defaultValue={
                                    pageData.students.home_country_city
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="name"
                                  name="home_country_state"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="State"
                                  defaultValue={
                                    pageData.students.home_country_state
                                  }
                                  InputContext={InputContext}
                                />
                                <SelectPopover
                                  name="home_country_country"
                                  grow
                                  required
                                  title="Country"
                                  isSearchable
                                  readOnly={!isEditable}
                                  defaultValue={countriesOptions.find(
                                    (country) =>
                                      country.value ===
                                      pageData.students.home_country_country
                                  )}
                                  options={countriesOptions}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            )}
                            {!isInitial && (
                              <InputLine title="Address in United States">
                                <PhoneNumberInput
                                  type="text"
                                  name="phone"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="USA Phone Number"
                                  value={pageData.students.phone}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="address"
                                  name="address"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="USA Address"
                                  defaultValue={pageData.students.address}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="code"
                                  name="zip"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="USA Zip Code"
                                  isZipCode
                                  defaultValue={pageData.students.zip}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            )}
                            {!isInitial && (
                              <InputLine>
                                <Input
                                  type="text"
                                  sanitize="name"
                                  name="city"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="USA City"
                                  defaultValue={pageData.students.city}
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  sanitize="name"
                                  name="state"
                                  grow
                                  required
                                  readOnly={!isEditable}
                                  title="USA State"
                                  defaultValue={pageData.students.state}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            )}
                          </Scope>
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "emergency-contact" && (
                        <InputLineGroup
                          title="Emergency Contact"
                          activeMenu={
                            pageData.activeMenu === "emergency-contact"
                          }
                        >
                          <Scope path={`enrollmentemergencies[0]`}>
                            <InputLine title="Emergency Contact">
                              <Input
                                type="text"
                                sanitize="person"
                                name="name"
                                required
                                grow
                                readOnly={!isEditable}
                                title="Full Name"
                                defaultValue={
                                  pageData.enrollmentemergencies.length > 0
                                    ? pageData.enrollmentemergencies[0].name
                                    : ""
                                }
                                InputContext={InputContext}
                              />
                              {pageData.enrollmentemergencies.length > 0 ? (
                                <SelectPopover
                                  name="relationship_type"
                                  required
                                  grow
                                  readOnly={!isEditable}
                                  title="Relationship Type"
                                  options={relationshipTypeOptions}
                                  isSearchable
                                  defaultValue={relationshipTypeOptions.find(
                                    (relationshipType) =>
                                      relationshipType.value ===
                                      pageData.enrollmentemergencies[0]
                                        .relationship_type
                                  )}
                                  InputContext={InputContext}
                                />
                              ) : (
                                <SelectPopover
                                  name="relationship_type"
                                  required
                                  grow
                                  readOnly={!isEditable}
                                  title="Relationship Type"
                                  options={relationshipTypeOptions}
                                  isSearchable
                                  InputContext={InputContext}
                                />
                              )}
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                sanitize="email"
                                name="email"
                                required
                                grow
                                readOnly={!isEditable}
                                title="E-mail"
                                defaultValue={
                                  pageData.enrollmentemergencies.length > 0
                                    ? pageData.enrollmentemergencies[0].email
                                    : ""
                                }
                                InputContext={InputContext}
                              />
                              <PhoneNumberInput
                                type="text"
                                name="phone"
                                required
                                grow
                                readOnly={!isEditable}
                                title="Phone Number"
                                value={
                                  pageData.enrollmentemergencies.length > 0
                                    ? pageData.enrollmentemergencies[0].phone
                                    : ""
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "enrollment-information" && (
                        <InputLineGroup
                          title="Enrollment Information"
                          activeMenu={
                            pageData.activeMenu === "enrollment-information"
                          }
                        >
                          <InputLine title="Enrollment Information">
                            {isGlobal ? (
                              <>
                                <DatePicker
                                  name="session_start_date"
                                  grow
                                  readOnly
                                  title="Session Start Date"
                                  defaultValue={
                                    pageData.session_start_date
                                      ? parseISO(pageData.session_start_date)
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="session_end_date"
                                  grow
                                  readOnly
                                  title="Session End Date"
                                  defaultValue={
                                    pageData.session_end_date
                                      ? parseISO(pageData.session_end_date)
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                                <Input name="first_tuition_due_date" grow readOnly
                                  title="First Tuition Due Date"
                                  defaultValue={pageData.first_tuition_due_date || ""}
                                  InputContext={InputContext} />
                                <SelectPopover
                                  name="plan_schedule"
                                  grow
                                  readOnly
                                  title="What is your plan schedule?"
                                  options={mergedScheduleOptions}
                                  isSearchable
                                  defaultValue={selectedPlanScheduleOption}
                                  InputContext={InputContext}
                                />
                              </>
                            ) : (
                              <>
                                <Input
                                  type="text"
                                  name="plan_months"
                                  required
                                  onlyInt
                                  readOnly={!isEditable}
                                  title="How many months do you plan to study?"
                                  defaultValue={pageData.plan_months}
                                  InputContext={InputContext}
                                />
                                <SelectPopover
                                  name="plan_schedule"
                                  required
                                  grow
                                  onChange={(option) =>
                                    setPlanScheduleWeekday(
                                      scheduleStartWeekdays[option?.value] ?? null
                                    )
                                  }
                                  readOnly={!isEditable}
                                  title="What is your plan schedule?"
                                  options={mergedScheduleOptions}
                                  isSearchable
                                  defaultValue={selectedPlanScheduleOption}
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="plan_date"
                                  required
                                  grow
                                  readOnly={!isEditable}
                                  title="What date do you wish to begin classes (M)?"
                                  defaultValue={
                                    pageData.plan_date
                                      ? parseISO(pageData.plan_date)
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  filterDate={
                                    planScheduleWeekday === null
                                      ? undefined
                                      : (date) => date.getDay() === planScheduleWeekday
                                  }
                                  InputContext={InputContext}
                                />
                              </>
                            )}
                          </InputLine>
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "dependent-information" && (
                        <InputLineGroup
                          title="Dependent Information"
                          activeMenu={
                            pageData.activeMenu === "dependent-information"
                          }
                        >
                          <InputLine title="Dependent Information">
                            <SelectPopover
                              key={hasDependentsKey}
                              name="has_dependents"
                              required
                              onChange={(el) => handleHasDependents(el)}
                              grow
                              disabled={!isEditable}
                              title="Do you have dependents?"
                              options={[
                                {
                                  value: null,
                                  label: "Please select...",
                                },
                                ...yesOrNoOptions,
                              ]}
                              defaultValue={
                                pageData.form_step === "dependent-information"
                                  ? null
                                  : yesOrNoOptions.find(
                                      (type) =>
                                        type.value === pageData.has_dependents
                                    )
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                          {pageData.has_dependents && (
                            <>
                              {pageData.enrollmentdependents.map(
                                (dependent, index) => {
                                  if (dependent && dependent.id !== 1) {
                                    return (
                                      <Scope
                                        key={index}
                                        path={`enrollmentdependents[${index}]`}
                                      >
                                        <InputLine
                                          title={`Dependent ${index + 1}`}
                                        >
                                          <Input
                                            type="hidden"
                                            name="id"
                                            defaultValue={dependent.id}
                                            InputContext={InputContext}
                                          />
                                          {isEditable ? (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveDependent(
                                                  index,
                                                  dependent.id
                                                )
                                              }
                                            >
                                              <Trash
                                                size={14}
                                                className="mt-4"
                                              />
                                            </button>
                                          ) : null}
                                          <Input
                                            type="text"
                                            sanitize="person"
                                            name="name"
                                            required
                                            grow
                                            title="Full Name"
                                            readOnly={!isEditable}
                                            defaultValue={dependent.name}
                                            InputContext={InputContext}
                                          />
                                          <SelectPopover
                                            name="gender"
                                            required
                                            grow
                                            title="Gender"
                                            readOnly={!isEditable}
                                            options={genderOptions}
                                            isSearchable
                                            defaultValue={genderOptions.find(
                                              (gender) =>
                                                gender.value ===
                                                dependent.gender
                                            )}
                                            InputContext={InputContext}
                                          />
                                          <SelectPopover
                                            name="relationship_type"
                                            required
                                            grow
                                            title="Relationship Type"
                                            readOnly={!isEditable}
                                            options={
                                              dependentRelationshipTypeOptions
                                            }
                                            isSearchable
                                            defaultValue={dependentRelationshipTypeOptions.find(
                                              (relationshipType) =>
                                                relationshipType.value ===
                                                dependent.relationship_type
                                            )}
                                            InputContext={InputContext}
                                          />
                                          <Input
                                            type="text"
                                            sanitize="email"
                                            name="email"
                                            required
                                            grow
                                            title="E-mail"
                                            readOnly={!isEditable}
                                            defaultValue={dependent.email}
                                            InputContext={InputContext}
                                          />
                                          <PhoneNumberInput
                                            type="text"
                                            name="phone"
                                            required
                                            grow
                                            title="Phone Number"
                                            readOnly={!isEditable}
                                            value={dependent.phone}
                                            InputContext={InputContext}
                                          />
                                        </InputLine>

                                        {pageData.dependentDocuments &&
                                          pageData.dependentDocuments.length >
                                            0 &&
                                          [...pageData.dependentDocuments]
                                            .filter((doc) => doc.active !== false)
                                            .sort((a, b) => {
                                              const numA = parseInt(a.title);
                                              const numB = parseInt(b.title);
                                              if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                              if (!isNaN(numA)) return -1;
                                              if (!isNaN(numB)) return 1;
                                              return 0;
                                            })
                                            .map((dependentDocument, docIndex) => (
                                              <FileInputV3
                                                key={docIndex}
                                                mode="dependent"
                                                document={dependentDocument}
                                                isEditable={isEditable}
                                                showChooseFile={!!(profile || pageData.lastActiveMenu.order < 7)}
                                                replacedWarning={(dependent.replacedWarnings || []).includes(dependentDocument.id)}
                                                savedFiles={(dependent.documents || []).filter(
                                                  (doc) => doc.document_id === dependentDocument.id
                                                )}
                                                pendingFiles={(dependent.pendingFiles || []).filter(
                                                  (pf) => pf.documentId === dependentDocument.id
                                                )}
                                                onAddPending={(files) => {
                                                  let shouldShowWarning = false;
                                                  setPageData((prev) => ({
                                                    ...prev,
                                                    enrollmentdependents: prev.enrollmentdependents.map((dep) => {
                                                      if (dep.id !== dependent.id) return dep;
                                                      const alreadyHasPending = (dep.pendingFiles || []).some((pf) => pf.documentId === dependentDocument.id);
                                                      const otherPending = (dep.pendingFiles || []).filter((pf) => pf.documentId !== dependentDocument.id);
                                                      const newForDoc = dependentDocument.multiple
                                                        ? [...(dep.pendingFiles || []).filter((pf) => pf.documentId === dependentDocument.id), ...files.map((f) => ({ file: f, name: f.name, documentId: dependentDocument.id }))]
                                                        : [{ file: files[0], name: files[0].name, documentId: dependentDocument.id }];
                                                      const newWarnings = !dependentDocument.multiple && alreadyHasPending
                                                        ? [...new Set([...(dep.replacedWarnings || []), dependentDocument.id])]
                                                        : (dep.replacedWarnings || []);
                                                      if (!dependentDocument.multiple && alreadyHasPending) shouldShowWarning = true;
                                                      return { ...dep, pendingFiles: [...otherPending, ...newForDoc], replacedWarnings: newWarnings };
                                                    }),
                                                  }));
                                                  if (shouldShowWarning) {
                                                    setTimeout(() => {
                                                      setPageData((p) => ({
                                                        ...p,
                                                        enrollmentdependents: p.enrollmentdependents.map((d) =>
                                                          d.id === dependent.id
                                                            ? { ...d, replacedWarnings: (d.replacedWarnings || []).filter((id) => id !== dependentDocument.id) }
                                                            : d
                                                        ),
                                                      }));
                                                    }, 3000);
                                                  }
                                                  setSuccessfullyUpdated(false);
                                                }}
                                                onRemovePending={(name) => {
                                                  setPageData((prev) => ({
                                                    ...prev,
                                                    enrollmentdependents: prev.enrollmentdependents.map((dep) =>
                                                      dep.id === dependent.id
                                                        ? { ...dep, pendingFiles: (dep.pendingFiles || []).filter((f) => !(f.documentId === dependentDocument.id && f.name === name)) }
                                                        : dep
                                                    ),
                                                  }));
                                                }}
                                                onDelete={(docId) => handleDeleteDependentDocument(dependent.id, docId)}
                                                onPendingChange={() => setSuccessfullyUpdated(false)}
                                              />
                                            ))}
                                      </Scope>
                                    );
                                  }
                                }
                              )}
                              {isEditable && (
                                <button
                                  type="button"
                                  onClick={() => handleAddDependent()}
                                  className="bg-slate-100 border ml-4 md:ml-6 py-2 px-2 text-xs flex flex-row justify-center items-center gap-2 rounded-md transition-all hover:border-primary hover:text-primary"
                                >
                                  <PlusCircle size={16} /> Add Dependent
                                </button>
                              )}
                            </>
                          )}
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "affidavit-of-support" && (
                        <InputLineGroup
                          title="Affidavit of Support"
                          activeMenu={
                            pageData.activeMenu === "affidavit-of-support"
                          }
                        >
                          <InputLine title="Proof of financial support">
                            {pageData.has_dependents ? (
                              <div className="text-lg">
                                {formatter.format(
                                  (parseFloat(
                                    pageData.filial
                                      .financial_support_student_amount
                                  ) +
                                    parseFloat(
                                      pageData.filial
                                        .financial_support_dependent_amount
                                    ) *
                                      pageData.enrollmentdependents.length) *
                                    pageData.plan_months
                                )}{" "}
                                (Estimate)
                              </div>
                            ) : (
                              <div className="text-lg">
                                {formatter.format(
                                  parseFloat(
                                    pageData.filial
                                      .financial_support_student_amount
                                  ) * pageData.plan_months
                                )}{" "}
                                (Estimate)
                              </div>
                            )}
                            <SelectPopover
                              name="need_sponsorship"
                              onChange={(el) => handleHasSponsors(el)}
                              required
                              disabled={!isEditable}
                              grow
                              title="Do you need sponsorship?"
                              options={[
                                {
                                  value: null,
                                  label: "Please select...",
                                },
                                ...sponsorshipOptions,
                              ]}
                              defaultValue={
                                pageData.form_step === "affidavit-of-support"
                                  ? null
                                  : sponsorshipOptions.find(
                                      (type) =>
                                        type.value === pageData.need_sponsorship
                                    )
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                          {pageData.need_sponsorship && (
                            <>
                              {pageData.enrollmentsponsors &&
                                pageData.enrollmentsponsors.map(
                                  (sponsor, index) => {
                                    if (sponsor) {
                                      return (
                                        <Scope
                                          key={index}
                                          path={`enrollmentsponsors[${index}]`}
                                        >
                                          <InputLine
                                            title={`Sponsor ${index + 1}`}
                                          >
                                            {sponsor.signature && (
                                              <button
                                                type="button"
                                                className="bg-mila_orange text-white p-2 rounded-sm relative top-2"
                                                onClick={() =>
                                                  handleGetAffidavitSupport(
                                                    sponsor.id
                                                  )
                                                }
                                              >
                                                Affidavit of Support
                                              </button>
                                            )}
                                            <Input
                                              type="hidden"
                                              name="id"
                                              defaultValue={sponsor.id}
                                              InputContext={InputContext}
                                            />
                                            {!sponsor.signature &&
                                            isEditable &&
                                            index ===
                                              pageData.enrollmentsponsors
                                                .length -
                                                1 ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleRemoveSponsor(
                                                    index,
                                                    sponsor.id
                                                  )
                                                }
                                              >
                                                <Trash
                                                  size={14}
                                                  className="mt-4"
                                                />
                                              </button>
                                            ) : null}
                                            <Input
                                              type="text"
                                              sanitize="person"
                                              name="name"
                                              required
                                              grow
                                              title="Full Name"
                                              readOnly={!isEditable}
                                              defaultValue={sponsor.name}
                                              InputContext={InputContext}
                                            />
                                            <SelectPopover
                                              name="relationship_type"
                                              required
                                              grow
                                              title="Relationship Type"
                                              options={
                                                sponsorRelationshipTypeOptions
                                              }
                                              isSearchable
                                              readOnly={!isEditable}
                                              onChange={(e) => {
                                                setSuccessfullyUpdated(false);
                                                if (
                                                  sponsorOtherRelationshipType.length >
                                                  0
                                                ) {
                                                  setSponsorOtherRelationshipType(
                                                    [
                                                      ...sponsorOtherRelationshipType.filter(
                                                        (sp) =>
                                                          sp.id !== sponsor.id
                                                      ),
                                                      {
                                                        id: sponsor.id,
                                                        value: e.value,
                                                      },
                                                    ]
                                                  );
                                                } else {
                                                  setSponsorOtherRelationshipType(
                                                    [
                                                      {
                                                        id: sponsor.id,
                                                        value: e.value,
                                                      },
                                                    ]
                                                  );
                                                }
                                              }}
                                              defaultValue={
                                                sponsorRelationshipTypeOptions.find(
                                                  (relationshipType) =>
                                                    relationshipType.value ===
                                                    sponsor.relationship_type
                                                ) ||
                                                sponsorRelationshipTypeOptions[
                                                  sponsorRelationshipTypeOptions.length -
                                                    1
                                                ]
                                              }
                                              InputContext={InputContext}
                                            />
                                            {sponsorOtherRelationshipType.find(
                                              (sp) => sp.id === sponsor.id
                                            ) !== undefined &&
                                            sponsorOtherRelationshipType.find(
                                              (sp) => sp.id === sponsor.id
                                            ) &&
                                            (!sponsorRelationshipTypeOptions.find(
                                              (relationshipType) =>
                                                relationshipType.value ===
                                                sponsorOtherRelationshipType.find(
                                                  (sp) => sp.id === sponsor.id
                                                ).value
                                            ) ||
                                              sponsorOtherRelationshipType.find(
                                                (sp) => sp.id === sponsor.id
                                              ).value === "Other") ? (
                                              <Input
                                                type="text"
                                                sanitize="name"
                                                name="relationship_type"
                                                required
                                                readOnly={!isEditable}
                                                grow
                                                title="Please specify"
                                                defaultValue={
                                                  sponsor.relationship_type
                                                }
                                                InputContext={InputContext}
                                              />
                                            ) : (
                                              <Input
                                                type="hidden"
                                                name="relationship_type"
                                                required
                                                grow
                                                title="Please specify"
                                                readOnly={!isEditable}
                                                defaultValue={
                                                  sponsorOtherRelationshipType &&
                                                  sponsorOtherRelationshipType.length >
                                                    0 &&
                                                  sponsorOtherRelationshipType.find(
                                                    (sp) => sp.id === sponsor.id
                                                  )?.value
                                                }
                                                InputContext={InputContext}
                                              />
                                            )}
                                          </InputLine>
                                          <InputLine>
                                            <Input
                                              type="text"
                                              sanitize="email"
                                              name="email"
                                              required
                                              grow
                                              title="E-mail"
                                              readOnly={!isEditable}
                                              defaultValue={sponsor.email}
                                              InputContext={InputContext}
                                            />
                                            <PhoneNumberInput
                                              type="text"
                                              name="phone"
                                              required
                                              grow
                                              title="Phone Number"
                                              readOnly={!isEditable}
                                              value={sponsor.phone}
                                              InputContext={InputContext}
                                            />
                                          </InputLine>
                                        </Scope>
                                      );
                                    }
                                  }
                                )}
                              {isEditable && (
                                <button
                                  type="button"
                                  onClick={() => handleAddSponsor()}
                                  className="bg-slate-100 border ml-4 md:ml-6 py-1 px-2 text-xs flex flex-row justify-center items-center gap-2 rounded-md transition-all hover:border-primary hover:text-primary"
                                >
                                  <PlusCircle size={16} /> Sponsor
                                </button>
                              )}
                            </>
                          )}
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "documents-upload" && (
                        <InputLineGroup
                          title="Documents Upload"
                          activeMenu={
                            pageData.activeMenu === "documents-upload"
                          }
                        >
                          {pageData.documents &&
                            pageData.documents.length > 0 &&
                            [...pageData.documents]
                              .filter((doc) => {
                                if (doc.active === false) return false;
                                if (doc.short_name === "St.Marriage certificate" && pageData.students?.marital_status !== "Married") return false;
                                return true;
                              })
                              .sort((a, b) => {
                                const numA = parseInt(a.title);
                                const numB = parseInt(b.title);
                                if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                                if (!isNaN(numA)) return -1;
                                if (!isNaN(numB)) return 1;
                                return 0;
                              })
                              .map((document, index) => (
                              <FileInputV3
                                key={index}
                                ref={(el) => (fileEnrollmentRefs.current[index] = el)}
                                document={document}
                                enrollmentdocuments={pageData.enrollmentdocuments}
                                enrollmentId={id}
                                isEditable={isEditable}
                                onDelete={handleDeleteDocument}
                                onDeleteDirect={handleDeleteDocumentDirect}
                                onPendingChange={() => setSuccessfullyUpdated(false)}
                              />
                            ))}
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "survey" && (
                        <InputLineGroup
                          title="Survey"
                          activeMenu={pageData.activeMenu === "survey"}
                        >
                          {pageData.survey_answered ? (
                            <InputLine title="Survey">
                              <div className="flex flex-1 items-center gap-2 p-4 text-sm text-gray-600">
                                <CheckCircle
                                  size={18}
                                  className="text-green-600"
                                />
                                You have already answered this survey.
                              </div>
                            </InputLine>
                          ) : (pageData.survey_questions || []).length === 0 ? (
                            <InputLine title="Survey">
                              <div className="flex flex-1 items-center gap-2 p-4 text-sm text-gray-600">
                                There are no survey questions to answer.
                              </div>
                            </InputLine>
                          ) : (
                            (() => {
                              const surveyQuestions =
                                pageData.survey_questions || [];
                              const totalQuestions = surveyQuestions.length;
                              const currentStep = Math.min(
                                surveyStep,
                                totalQuestions - 1
                              );
                              const isLastQuestion =
                                currentStep >= totalQuestions - 1;
                              return (
                                <div className="flex w-full flex-col gap-6 px-2 py-4 md:px-4">
                                  <div className="flex w-full flex-col gap-2">
                                    <div className="flex w-full flex-row items-center justify-between">
                                      <span className="text-sm font-bold text-mila_orange">
                                        Question {currentStep + 1} of{" "}
                                        {totalQuestions}
                                      </span>
                                      <span className="text-xs font-medium text-gray-400">
                                        {Math.round(
                                          ((currentStep + 1) / totalQuestions) *
                                            100
                                        )}
                                        %
                                      </span>
                                    </div>
                                    <div className="flex w-full flex-row items-center gap-1">
                                      {surveyQuestions.map((q, index) => (
                                        <div
                                          key={q.id}
                                          className={`h-1.5 flex-1 rounded-full transition-all ${
                                            index < currentStep
                                              ? "bg-green-400"
                                              : index === currentStep
                                              ? "bg-mila_orange"
                                              : "bg-gray-200"
                                          }`}
                                        />
                                      ))}
                                    </div>
                                  </div>

                                  <div className="w-full">
                                    {surveyQuestions.map((question, index) => {
                                      const questionOptions = (
                                        question.options || []
                                      ).map((option) => ({
                                        value: getOptionValue(option),
                                        label: option.option_description,
                                      }));
                                      return (
                                        <div
                                          key={question.id}
                                          className={
                                            index === currentStep
                                              ? "w-full"
                                              : "hidden"
                                          }
                                        >
                                          <div className="flex w-full flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
                                            <div className="flex w-full flex-row items-start gap-3">
                                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-mila_orange text-sm font-bold text-white">
                                                {index + 1}
                                              </span>
                                              <div className="flex flex-1 flex-col">
                                                <p className="pt-1 text-base font-semibold leading-snug text-gray-700 break-words">
                                                  {question.description}
                                                  <span className="text-red-600">
                                                    {" "}
                                                    *
                                                  </span>
                                                </p>
                                                {question.instruction ? (
                                                  <p className="text-xs font-medium leading-snug text-gray-400 break-words">
                                                    {question.instruction}
                                                  </p>
                                                ) : null}
                                              </div>
                                            </div>
                                            <div className="flex w-full flex-col pl-0 md:pl-11">
                                              {question.type === "Text Area" ? (
                                                <textarea
                                                  value={
                                                    surveyAnswers[
                                                      question.id
                                                    ] || ""
                                                  }
                                                  onChange={(e) =>
                                                    handleSurveyAnswer(
                                                      question.id,
                                                      e.target.value
                                                    )
                                                  }
                                                  rows={4}
                                                  maxLength={255}
                                                  className="w-full rounded-md border border-gray-300 p-2 px-3 text-sm focus:border-mila_orange focus:outline-none focus:ring-1 focus:ring-mila_orange"
                                                />
                                              ) : (
                                                <div className="flex w-full flex-col gap-2">
                                                  {questionOptions.map(
                                                    (option) => {
                                                      const isMulti =
                                                        question.type ===
                                                        "Pick Many";
                                                      const checked =
                                                        isOptionChecked(
                                                          question,
                                                          option.value
                                                        );
                                                      const isOtherOption = (
                                                        question.options || []
                                                      ).some(
                                                        (o) =>
                                                          o.is_other &&
                                                          String(
                                                            getOptionValue(o)
                                                          ) ===
                                                            String(option.value)
                                                      );
                                                      return (
                                                        <div
                                                          key={option.value}
                                                          className="flex w-full flex-col"
                                                        >
                                                          <label
                                                            className={`flex w-full cursor-pointer flex-row items-center gap-3 rounded-md border p-3 text-sm transition-all ${
                                                              checked
                                                                ? "border-mila_orange bg-orange-50 text-gray-800"
                                                                : "border-gray-200 bg-white text-gray-600 hover:border-mila_orange/50"
                                                            }`}
                                                          >
                                                            <input
                                                              type={
                                                                isMulti
                                                                  ? "checkbox"
                                                                  : "radio"
                                                              }
                                                              name={`survey-${question.id}`}
                                                              value={
                                                                option.value
                                                              }
                                                              checked={checked}
                                                              onChange={() =>
                                                                isMulti
                                                                  ? handleSurveyPickMany(
                                                                      question,
                                                                      option.value
                                                                    )
                                                                  : handleSurveyPickOne(
                                                                      question,
                                                                      option.value
                                                                    )
                                                              }
                                                              className="h-4 w-4 accent-mila_orange"
                                                            />
                                                            <span className="flex-1 break-words">
                                                              {option.label}
                                                            </span>
                                                          </label>
                                                          {isOtherOption &&
                                                            checked && (
                                                              <input
                                                                type="text"
                                                                value={
                                                                  surveyOtherText[
                                                                    question.id
                                                                  ] || ""
                                                                }
                                                                onChange={(e) =>
                                                                  handleSurveyOtherText(
                                                                    question.id,
                                                                    e.target
                                                                      .value
                                                                  )
                                                                }
                                                                placeholder="Please specify"
                                                                maxLength={255}
                                                                className="ml-7 mt-2 w-[calc(100%-1.75rem)] rounded-md border border-gray-300 p-2 px-3 text-sm focus:border-mila_orange focus:outline-none focus:ring-1 focus:ring-mila_orange"
                                                              />
                                                            )}
                                                        </div>
                                                      );
                                                    }
                                                  )}
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  <div className="flex w-full flex-row items-center justify-between gap-2">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSurveyStep((step) =>
                                          Math.max(step - 1, 0)
                                        )
                                      }
                                      disabled={currentStep === 0}
                                      className={`flex flex-row items-center justify-center gap-1 rounded-md px-5 py-2.5 text-sm font-bold transition-all ${
                                        currentStep === 0
                                          ? "cursor-not-allowed bg-gray-100 text-gray-400"
                                          : "bg-slate-200 text-slate-700 hover:bg-slate-300 active:bg-slate-400"
                                      }`}
                                    >
                                      <ChevronLeft size={16} />
                                      <span>Previous</span>
                                    </button>
                                    {!isLastQuestion && (
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (
                                            !validateSurveyQuestion(
                                              surveyQuestions[currentStep]
                                            )
                                          ) {
                                            return;
                                          }
                                          setSurveyStep((step) =>
                                            Math.min(
                                              step + 1,
                                              totalQuestions - 1
                                            )
                                          );
                                        }}
                                        className="flex flex-row items-center justify-center gap-1 whitespace-nowrap rounded-md bg-mila_orange px-5 py-4 text-sm font-bold text-white transition-all hover:bg-orange-500 active:bg-orange-600"
                                      >
                                        <span>Next</span>
                                        <ChevronRight size={16} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })()
                          )}
                        </InputLineGroup>
                      )}
                      {pageData.activeMenu === "student-signature" && (
                        <InputLineGroup
                          title="Student Signature"
                          activeMenu={
                            pageData.activeMenu === "student-signature"
                          }
                        >
                          {!pageData.signature ? (
                            <InputLine
                              title={
                                <span className="flex items-center gap-1">
                                  Student Signature
                                  <span className="text-red-600">*</span>
                                </span>
                              }
                            >
                              {pageData.contracts &&
                                pageData.contracts.length > 0 && (
                                  <div className="flex flex-col gap-2 flex-1">
                                    {console.log("[STUDENT SIGNATURE DEBUG]", {
                                      isGlobal,
                                      wantedSubtype: isGlobal
                                        ? "Non-F1 Global Contract"
                                        : "F1 Contract",
                                      contracts: pageData.contracts,
                                      availableSubtypes: pageData.contracts?.map(
                                        (c) => c?.file?.document?.subtype
                                      ),
                                    })}
                                    <PDFViewer
                                      download={true}
                                      file={{
                                        url: pageData.contracts?.find(
                                          (contract) =>
                                            contract.file.document.subtype ===
                                            (isGlobal
                                              ? "Non-F1 Global Contract"
                                              : "F1 Contract")
                                        )?.file?.url,
                                      }}
                                      height={450}
                                    />
                                    <CheckboxInput
                                      name="agreement"
                                      required={true}
                                      title="I agree to the terms and conditions above"
                                      InputContext={InputContext}
                                      readOnly={!isEditable}
                                      grow
                                    />
                                  </div>
                                )}
                              <div className={`flex flex-col items-start justify-start gap-3 ${isMobile ? "w-full" : "sm:w-1/2 w-1/3"}`}>
                                <p className="text-sm text-gray-600 italic flex items-center gap-1">
                                  Please sign in the box below using your mouse,
                                  touchpad, or touch screen
                                  <span className="text-red-600">*</span>
                                </p>
                                <div
                                  onClick={() => setSuccessfullyUpdated(false)}
                                  className={`w-full gap-2 border-2 border-gray-300 rounded-lg shadow-sm hover:border-mila_orange transition-colors cursor-crosshair bg-white ${isMobile ? "h-48" : "h-64 md:h-80 lg:h-96 max-w-full lg:max-w-4xl"}`}
                                  style={isMobile ? { position: "relative" } : {}}
                                >
                                  <SignaturePad
                                    redrawOnResize
                                    ref={signatureRef}
                                    options={{
                                      backgroundColor: "#FFF",
                                      penColor: "#111",
                                      minWidth: 1,
                                      maxWidth: 3,
                                      throttle: 0,
                                      velocityFilterWeight: 0.7,
                                    }}
                                    canvasProps={isMobile ? { style: { width: "100%", height: "100%" } } : {}}
                                  />
                                </div>
                                <div className="flex flex-row items-center justify-start gap-3">
                                  <button
                                    type="button"
                                    onClick={handleClearSignature}
                                    className="bg-red-500 hover:bg-red-600 text-white rounded-md py-2 px-6 flex flex-row items-center justify-center text-sm gap-2 transition-colors"
                                  >
                                    <X size={18} />
                                    Clear Signature
                                  </button>
                                </div>
                              </div>
                            </InputLine>
                          ) : (
                            <InputLine title="Student Signature">
                              <div className="flex flex-1 flex-col items-start justify-start w-full">
                                <img
                                  src={pageData?.signature?.url}
                                  className="border w-full max-w-96"
                                />
                                <p className="text-xs p-2 border border-t-0 rounded-b-md bg-slate-100 w-full max-w-96">
                                  Signed:{" "}
                                  {format(
                                    parseISO(pageData.signature.created_at),
                                    "MMM do, yyyy"
                                  )}{" "}
                                  at{" "}
                                  {format(
                                    parseISO(pageData.signature.created_at),
                                    "HH:mm"
                                  )}
                                </p>
                              </div>
                            </InputLine>
                          )}
                        </InputLineGroup>
                      )}
                      {(pageData.activeMenu === "sponsor-signature" ||
                        pageData.activeMenu === "finished") && (
                        <InputLineGroup
                          title="Finish"
                          activeMenu={
                            pageData.activeMenu === "sponsor-signature" ||
                            pageData.activeMenu === "finished"
                          }
                        >
                          <div className="flex flex-1 w-full flex-col items-center justify-center text-center gap-4">
                            <div className="flex w-full flex-row items-center justify-center text-center gap-4">
                              <CheckCircle size={32} color="#00b361" />
                              <span className="text-lg font-bold">
                                Thank you!
                              </span>
                            </div>
                            <div className="flex w-full flex-row items-center justify-center text-center gap-4">
                              <span>
                                Your request has been sent successfully!
                              </span>
                            </div>
                          </div>
                        </InputLineGroup>
                      )}
                    </>
                  ) : (
                    <FormLoading />
                  )}

                  {pageData.loaded && pageData.activeMenu !== "finished" && (
                    <div className="sticky bottom-0 lg:hidden flex flex-col gap-2 bg-white border-t border-gray-200 mt-4 shadow-lg">
                      <div className="flex flex-row items-center justify-center gap-1 pt-3 px-4">
                        {getVisibleMenus().map((menu, index) => (
                          <div
                            key={menu.name}
                            className={`h-1.5 flex-1 rounded-full transition-all ${
                              menu.name === pageData.activeMenu
                                ? "bg-mila_orange"
                                : menu.order <=
                                  (pageData.lastActiveMenu?.order || 0)
                                ? "bg-green-400"
                                : "bg-gray-200"
                            }`}
                          />
                        ))}
                      </div>
                      <div className="flex flex-row items-center justify-between gap-2 p-4">
                        <button
                          type="button"
                          onClick={handlePreviousMenu}
                          disabled={isFirstMenu()}
                          className={`flex flex-row items-center justify-center gap-1 px-4 py-2 rounded-md text-sm font-bold transition-all ${
                            isFirstMenu()
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-slate-200 text-slate-700 hover:bg-slate-300 active:bg-slate-400"
                          }`}
                        >
                          <ChevronLeft size={16} />
                          <span>Previous</span>
                        </button>
                        <div className="text-xs text-gray-500 font-medium">
                          {getVisibleMenus().findIndex(
                            (m) => m.name === pageData.activeMenu
                          ) + 1}{" "}
                          / {getVisibleMenus().length}
                        </div>
                        {!successfullyUpdated ? (
                          <button
                            type="submit"
                            disabled={
                              pageData.activeMenu === "survey" &&
                              !pageData.survey_answered &&
                              (pageData.survey_questions || []).length > 0 &&
                              !surveyComplete
                            }
                            className={`flex flex-row items-center justify-center gap-1 px-4 py-2 rounded-md text-sm font-bold transition-all whitespace-nowrap ${
                              pageData.activeMenu === "survey" &&
                              !pageData.survey_answered &&
                              (pageData.survey_questions || []).length > 0 &&
                              !surveyComplete
                                ? "bg-gray-300 text-white cursor-not-allowed"
                                : "bg-mila_orange text-white hover:bg-orange-500 active:bg-orange-600"
                            }`}
                          >
                            <Save size={16} />
                            <span>Save & Continue</span>
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={handleNextMenu}
                            disabled={isLastMenu()}
                            className={`flex flex-row items-center justify-center gap-1 px-4 py-2 rounded-md text-sm font-bold transition-all whitespace-nowrap ${
                              isLastMenu()
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                : "bg-mila_orange text-white hover:bg-orange-500 active:bg-orange-600"
                            }`}
                          >
                            <span>Next</span>
                            <ChevronRight size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
